import { Inject, Injectable, Optional } from '@angular/core';
import { InjectionToken } from '@angular/core';

import { deviceService } from '@unpispas/upp-base';
import { adhocService } from '@unpispas/upp-base';

import { dataService } from '@unpispas/upp-data';
import { PrintDevice } from '@unpispas/upp-data';
import { RasppiView } from '@unpispas/upp-data';
import { Pinpad } from '@unpispas/upp-data';
import type { PinpadPay } from '@unpispas/upp-data';

import { paytefService } from '@unpispas/feature/drivers';

import { htmlBuilder } from './tohtml/builder';

import { StyleFormat } from './styles/ticket.style';
import { ticketStyle } from './styles/ticket.style';
import { auditStyle } from './styles/audit.style';
import { reportStyle } from './styles/report.style';
import { productStyle } from './styles/product.style';
import { monthlyStyle } from './styles/monthly.style';
import { clientsStyle } from './styles/clients.style';
import { menuStyle } from './styles/menu.style';

/** Terminal receipt profile: larger body type; QR capped at TicketBAI max (30–40 mm). */
const TERMINAL_SCALE = 1.5;
const TERMINAL_QRSIZE_MM = 40;
/** Paper width when QueryPrinter does not report widthMm. */
const TERMINAL_WIDTH_FALLBACK_MM = 58;

/********************************/
/* Paytef terminal print channel */
/********************************/

/**
 * QueryPrinter profile, HTML/voucher send, and per-terminal FIFO.
 * Owned once by {@link printService}; protocol lives in {@link paytefService}.
 */
class PinpadPrint {
    /** Tail of the print chain of each terminal; always settles without rejecting. */
    private readonly _terminals = new WeakMap<Pinpad, Promise<void>>();

    constructor(private readonly paytef: paytefService) {}

    /**
     * Chains `job` after the previous job of the same terminal (FIFO). The terminal runs a single
     * operation at a time, so jobs are serialized from request to completion; a failing job never
     * blocks the next one. Physical `PrintHtml` / voucher send do not enqueue themselves — the
     * orchestrator wraps multi-step work (render + send) with this method.
     */
    RunOnTerminal<T>(terminal: Pinpad, job: () => Promise<T>): Promise<T> {
        const _previous = this._terminals.get(terminal) ?? Promise.resolve();
        const _run = _previous.then(job);
        this._terminals.set(terminal, _run.then(() => undefined, () => undefined));
        return _run;
    }

    /**
     * Resolves the ticket {@link StyleFormat} for `terminal`, or `null` when the printer is absent
     * (`present === false`) so the caller skips HTML build and send.
     */
    async QueryStyle(terminal: Pinpad): Promise<StyleFormat | null> {
        const _info = await this.paytef.QueryPrinter(terminal);
        if (_info.present === false) {
            return null;
        }

        return {
            size: _info.widthMm ?? TERMINAL_WIDTH_FALLBACK_MM,
            margin: [0, 0, 0, 0],
            scale: TERMINAL_SCALE,
            qrsize: TERMINAL_QRSIZE_MM,
        };
    }

    /**
     * Sends pre-built HTML to the terminal printer. Does not join the FIFO — use
     * {@link RunOnTerminal} when the job includes render or other steps.
     */
    async PrintHtml(terminal: Pinpad, html: string): Promise<boolean> {
        const _result = await this.paytef.Print(terminal, html);
        return _result.printed === true;
    }

    /**
     * Reprints a stored card voucher on `terminal`, serialized on that terminal's FIFO.
     */
    PrintVoucher(terminal: Pinpad, row: PinpadPay, copy: 'merchant' | 'customer'): Promise<boolean> {
        return this.RunOnTerminal(terminal, async () => {
            const _result = await this.paytef.PrintVoucher(terminal, row, copy);
            return _result.printed === true;
        });
    }
}

/**
 * Application hook that forwards serialized ticket (or future command) markup to the platform print
 * pipeline (Cordova/Capacitor, browser print, ESC/POS bridge, etc.). Provide a concrete binding from
 * the shell; when omitted, {@link printService} still produces HTML but does not spool jobs.
 */
export interface PrintPort {
    /**
     * Hands off ticket HTML produced by {@link printService}. Implementations may inline critical
     * styles, split body/head, or enqueue native print APIs.
     *
     * @param html Printable HTML document (doctype + head/body) ready for print backends.
     */
    submitTicketHtml(html: string): Promise<void>;
}

/**
 * Injection token for {@link PrintPort}. Optional at runtime: absence means HTML-only rendering
 * until the host application registers an adapter.
 */
export const PRINT_PORT = new InjectionToken<PrintPort>('PRINT_PORT');

/**
 * Cross-cutting print facade: serializes HTML from a DOM host and dispatches to paper
 * (`PrintDevice` / PRINT_PORT / browser) or to a Paytef terminal via the owned {@link PinpadPrint}
 * channel. Does not create print components. Destination policy (BAR vs terminal vs browser)
 * belongs to the caller (e.g. ticket `queueService`).
 */
@Injectable({
    providedIn: 'root',
})
export class printService {
    private _pinpad: PinpadPrint | null = null;

    constructor(
        @Optional() @Inject(PRINT_PORT) private readonly output: PrintPort | null,
        private readonly devices: deviceService,
        private readonly data: dataService,
        private readonly adhoc: adhocService,
        private readonly paytef: paytefService,
    ) {}

    /** Lazy Paytef channel; created on first terminal print / FIFO / voucher use. */
    private get Pinpad(): PinpadPrint {
        return this._pinpad ??= new PinpadPrint(this.paytef);
    }

    /**
     * Serializes `nativeElement` and dispatches the job to `where`.
     *
     * - {@link Pinpad}: recognized Paytef model only (`instanceof Pinpad`) — QueryPrinter profile
     *   then Print. Unrecognized shapes throw. Does not join the terminal FIFO; wrap multi-step
     *   jobs with {@link RunOnTerminal}.
     * - {@link PrintDevice}: physical path only — opens WS and `doPrint`; returns `true` only on SUCCESS.
     *   Missing WS or doPrint failure returns `false` (no browser handoff that would fake kitchen success).
     * - `null`: `PRINT_PORT` when registered, otherwise browser iframe (preview / no till printer).
     *
     * Where policy (BAR vs terminal vs browser, retries, fallbacks) belongs to the caller.
     *
     * @returns `true` when the job completed successfully.
     */
    async printTicket(nativeElement: HTMLElement, where: PrintDevice | Pinpad | null = null, styleFormat: StyleFormat | null = null): Promise<boolean> {
        if (!nativeElement) {
            throw new Error('[TICKET PRINT] NativeElement is required');
        }

        const _pinpad = where instanceof Pinpad ? where : null;
        const _device = where instanceof PrintDevice ? where : null;
        
        if (where != null && !_pinpad && !_device) {
            throw new Error('[TICKET PRINT] print destination is not recognized');
        }

        let _style: StyleFormat | null;
        if (_pinpad) {
            _style = await this.Pinpad.QueryStyle(_pinpad);
            if (!_style) {
                return false;
            }
        }
        else {
            _style = styleFormat ?? (_device ? { size: 80 as const, margin: [0, 0, 0, 0] as [number, number, number, number] } : null);
        }

        const _css = ticketStyle(_style);
        const _html = await new htmlBuilder(nativeElement).BuildHtml(_css, this._ResolveTicketWrapperClass(_css));

        if (_pinpad) {
            return this.Pinpad.PrintHtml(_pinpad, _html);
        }

        if (_device) {
            return this._PrintToDevice(_device, _html);
        }

        if (this.output) {
            console.log('[TICKET PRINT] Job sent to printer: PRINT_PORT');
            await this.output.submitTicketHtml(_html);
            return true;
        }

        console.log('[TICKET PRINT] Job sent to printer: BROWSER');
        await this.browserPrintTicket(_html);
        return true;
    }

    /**
     * Per-terminal FIFO for Paytef print jobs (receipt render+send, voucher, etc.).
     */
    RunOnTerminal<T>(terminal: Pinpad, job: () => Promise<T>): Promise<T> {
        return this.Pinpad.RunOnTerminal(terminal, job);
    }

    /**
     * Reprints a stored card voucher on `terminal` (FIFO). Returns whether Paytef printed.
     */
    PrintVoucher(
        terminal: Pinpad,
        row: PinpadPay,
        copy: 'merchant' | 'customer',
    ): Promise<boolean> {
        return this.Pinpad.PrintVoucher(terminal, row, copy);
    }

    /**
     * Serializes `nativeElement` with the same HTML/CSS contract as {@link printTicket} and
     * requests a rendered PDF from the guest ticket-export endpoint.
     *
     * @returns A downloadable PDF `Blob`. Throws when the element is missing or the export fails.
     */
    async TicketToPdf(nativeElement: HTMLElement, styleFormat: StyleFormat | null = null): Promise<Blob> {
        return this._ElementToPdf('guest/ticketpdf', nativeElement, styleFormat);
    }

    /**
     * Staff channel: same HTML/CSS as {@link printTicket}, rendered via `POST /ticket/invoicepdf`.
     *
     * @returns A downloadable PDF `Blob`. Throws when the element is missing or the export fails.
     */
    async InvoiceToPdf(nativeElement: HTMLElement, styleFormat: StyleFormat | null = null): Promise<Blob> {
        return this._ElementToPdf('ticket/invoicepdf', nativeElement, styleFormat);
    }

    /**
     * Builds (or reuses) an invoice PDF and emails it via `POST /ticket/invoicesend`
     * or `POST /guest/invoicesend` when `guest` is true.
     *
     * @returns Server `errorcode` (0 = Success). Does not throw on application errorcodes.
     */
    async SendInvoiceEmail(opts: {
        element?: HTMLElement | null;
        blob?: Blob | null;
        lang: string;
        recipient: string;
        invoiceName: string;
        ticketObjid?: string | number | null;
        styleFormat?: StyleFormat | null;
        guest?: boolean;
    }): Promise<number> {
        let _blob = opts.blob ?? null;
        if (!_blob) {
            if (!opts.element) {
                throw new Error('[TICKET PRINT] NativeElement or blob is required');
            }
            _blob = opts.guest
                ? await this.TicketToPdf(opts.element, opts.styleFormat ?? null)
                : await this.InvoiceToPdf(opts.element, opts.styleFormat ?? null);
        }

        const _pdfdata = await this._BlobToBase64(_blob);
        const _params: Record<string, string> = {
            lang: opts.lang,
            recipient: opts.recipient,
            invoice: opts.invoiceName,
        };
        if (opts.ticketObjid != null && String(opts.ticketObjid).trim() !== '') {
            _params['ticket'] = String(opts.ticketObjid);
        }

        const _path = opts.guest ? 'guest/invoicesend' : 'ticket/invoicesend';
        const _response: any = await this.adhoc.DoRequest(_path, _params, { pdfdata: _pdfdata }, true);
        if (!_response || _response['errorcode'] == null) {
            throw new Error('[TICKET PRINT] Invoice email send failed');
        }
        return Number(_response['errorcode']);
    }

    private async _ElementToPdf(
        path: string,
        nativeElement: HTMLElement,
        styleFormat: StyleFormat | null,
    ): Promise<Blob> {
        if (!nativeElement) {
            throw new Error('[TICKET PRINT] NativeElement is required');
        }

        const _css = ticketStyle(styleFormat);
        const _html = await new htmlBuilder(nativeElement).BuildHtml(_css, this._ResolveTicketWrapperClass(_css));

        const _response: any = await this.adhoc.DoRequest(path, null, { html: _html }, true);
        if (!_response || (_response['errorcode'] != 0) || !_response['pdfdata']) {
            throw new Error('[TICKET PRINT] PDF export failed');
        }

        const _binary = atob(_response['pdfdata']);
        const _bytes = new Uint8Array(_binary.length);
        for (let i = 0; i < _binary.length; i++) {
            _bytes[i] = _binary.charCodeAt(i);
        }

        return new Blob([_bytes], { type: 'application/pdf' });
    }

    private _BlobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const _reader = new FileReader();
            _reader.onload = () => {
                const _dataUrl = String(_reader.result || '');
                const _comma = _dataUrl.indexOf(',');
                resolve(_comma >= 0 ? _dataUrl.slice(_comma + 1) : '');
            };
            _reader.onerror = () => reject(_reader.error || new Error('[TICKET PRINT] Blob read failed'));
            _reader.readAsDataURL(blob);
        });
    }

    /**
     * `.upp-ticket-print` variant class for the ticket-family contract (`printTicket` and the
     * `audit` report style, which reuses it intentionally — see `audit.style.ts`).
     */
    private _ResolveTicketWrapperClass(css: string): string {
        if (/@page\s*\{[^}]*size:\s*A5\b/i.test(css)) {
            return 'upp-ticket-print upp-ticket-print--a5';
        }
        return 'upp-ticket-print upp-ticket-print--thermal';
    }

    private async _PrintToDevice(device: PrintDevice, html: string): Promise<boolean> {
        const _deviceId = String(device.device || '').trim();
        if (!_deviceId){
            console.error('[TICKET PRINT] printer has no device id');
            return false;
        }

        const _rasppiView = device.rasppi ? this.data.alives.get(device.rasppi) as RasppiView | null : null;
        const _connection = _rasppiView ? await _rasppiView.WSOpen(this.devices) : null;
        if (!_connection){
            console.error("[TICKET PRINT] cannot open WS for printer '" + _deviceId + "'");
            return false;
        }

        const _copies = Number(device.copies) > 0 ? Number(device.copies) : 1;

        try {
            const _response = await this.devices.doPrint(_connection, _deviceId, html, _copies);
            if (!_response || (_response.exec !== 'SUCCESS')){
                console.error(
                    "[TICKET PRINT] doPrint failed for '" + _deviceId + "' (" +
                    (_response ? _response.data : 'no response') + ")",
                );
                return false;
            }

            console.info("[TICKET PRINT] dispatched to '" + _deviceId + "'");
            return true;
        }
        catch (err) {
            console.error("[TICKET PRINT] doPrint threw for '" + _deviceId + "'", err);
            return false;
        }
        finally {
            _connection.Disconnect();
        }
    }

    private async browserPrintTicket(printableDocument: string): Promise<void> {
        if ((typeof window === 'undefined') || (typeof document === 'undefined')) {
            return;
        }

        const parsedDocument = new DOMParser().parseFromString(printableDocument, 'text/html');
        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';

        document.body.appendChild(iframe);

        const frameWindow = iframe.contentWindow;
        const frameDocument = frameWindow?.document || null;
        if (!frameWindow || !frameDocument) {
            document.body.removeChild(iframe);
            throw new Error('[TICKET PRINT] Cannot initialize iframe print context');
        }

        frameDocument.head.innerHTML = parsedDocument.head.innerHTML;
        frameDocument.body.innerHTML = parsedDocument.body.innerHTML;
        if (typeof frameDocument.close === 'function') {
            frameDocument.close();
        }

        await this.waitForFrameImages(frameDocument);

        frameWindow.focus();
        let isRemoved = false;
        const removeIframe = (): void => {
            if (isRemoved) {
                return;
            }

            isRemoved = true;
            document.body.removeChild(iframe);
        };

        frameWindow.onafterprint = () => {
            removeIframe();
        };
        frameWindow.print();

        /**
         * Fallback cleanup for environments where `afterprint` is not emitted.
         */
        window.setTimeout(() => {
            removeIframe();
        }, 500);
    }

    /**
     * Builds a standalone report HTML document from an already rendered host element and
     * sends it to the browser print dialog through a hidden iframe.
     */
    async PrintReport(
        title: string,
        nativeElement: HTMLElement,
        style: 'income' | 'product' | 'audit' | 'monthly' | 'clients' | 'menu',
    ): Promise<void> {
        if (!nativeElement) {
            throw new Error('[REPORT PRINT] NativeElement is required');
        }

        let css: string;
        let wrapperClass: string | null = null;
        switch (style) {
            case 'income':
                css = reportStyle();
                break;
            case 'product':
                css = productStyle();
                break;
            case 'audit':
                // Shares the ticket `.upp-ticket-print` contract by design (see audit.style.ts).
                css = auditStyle();
                wrapperClass = this._ResolveTicketWrapperClass(css);
                break;
            case 'monthly':
                css = monthlyStyle();
                break;
            case 'clients':
                css = clientsStyle();
                break;
            case 'menu':
                css = menuStyle();
                break;
            default:
                throw new Error(`[REPORT PRINT] Unsupported report style: ${String(style)}`);
        }

        const baseDocument = await new htmlBuilder(nativeElement).BuildHtml(css, wrapperClass);
        const safeTitle = String(title || 'Report')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

        const printableDocument = baseDocument.replace('<title>Ticket</title>', `<title>${safeTitle}</title>`);
        await this.browserPrintReport(printableDocument);
    }

    private async browserPrintReport(printableDocument: string): Promise<void> {
        if ((typeof window === 'undefined') || (typeof document === 'undefined')) {
            return;
        }

        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';

        document.body.appendChild(iframe);

        const frameWindow = iframe.contentWindow;
        const frameDocument = frameWindow?.document || null;
        if (!frameWindow || !frameDocument) {
            document.body.removeChild(iframe);
            return;
        }

        const parsedDocument = new DOMParser().parseFromString(printableDocument, 'text/html');
        frameDocument.head.innerHTML = parsedDocument.head.innerHTML;
        frameDocument.body.innerHTML = parsedDocument.body.innerHTML;
        if (typeof frameDocument.close === 'function') {
            frameDocument.close();
        }

        await this.waitForFrameImages(frameDocument);
        await this.waitForFrameFonts(frameDocument);
        frameWindow.focus();
        let isRemoved = false;
        const removeIframe = (): void => {
            if (isRemoved) {
                return;
            }

            isRemoved = true;
            document.body.removeChild(iframe);
        };

        frameWindow.onafterprint = () => {
            removeIframe();
        };
        frameWindow.print();

        /**
         * Fallback cleanup for environments where `afterprint` is not emitted.
         */
        window.setTimeout(() => {
            removeIframe();
        }, 500);
    }

    /**
     * Waits for `@font-face` loads in the print iframe before invoking `print()`.
     * Resolves immediately when the document has no `fonts` API.
     */
    private async waitForFrameFonts(frameDocument: Document, timeoutMs = 1500): Promise<void> {
        const fonts = (frameDocument as Document & {
            fonts?: { ready?: Promise<unknown> };
        }).fonts;
        if (!fonts?.ready || typeof fonts.ready.then !== 'function') {
            return;
        }

        await Promise.race([
            Promise.resolve(fonts.ready).catch(() => undefined),
            new Promise<void>((resolve) => {
                window.setTimeout(resolve, timeoutMs);
            }),
        ]);
    }

    private async waitForFrameImages(frameDocument: Document, timeoutMs = 1500): Promise<void> {
        const images = frameDocument.images
            ? Array.from(frameDocument.images)
            : ((typeof frameDocument.querySelectorAll === 'function')
                ? Array.from(frameDocument.querySelectorAll('img'))
                : []);
        if (images.length === 0) {
            return;
        }

        const waitAllImages = Promise.all(images.map((image) => {
            if (image.complete) {
                return Promise.resolve();
            }

            const loadPromise = new Promise<void>((resolve) => {
                const done = (): void => resolve();
                image.addEventListener('load', done, { once: true });
                image.addEventListener('error', done, { once: true });
            });

            if (typeof image.decode === 'function') {
                return Promise.race([
                    image.decode().catch(() => undefined),
                    loadPromise,
                ]).then(() => undefined);
            }

            return loadPromise;
        }));

        await Promise.race([
            waitAllImages,
            new Promise<void>((resolve) => {
                window.setTimeout(resolve, timeoutMs);
            }),
        ]);
    }

}
